import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';
import type { Keyframes } from 'styled-components/dist/types';

import { device, hexToRGB } from '@/styles/helpers';

const shimmerAnimate = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    transform: translateX(0%);
    opacity: 0.7;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const scale = keyframes`
	0%, 5%, 10%,15%, 25%, 40%, 100% {transform: scale(1);}
	7% {transform: scale(1.025);}
	13% {transform: scale(1.015);}
`;

const shine = keyframes`
	0% ,15%{left: -400%}
	25% {left: 400%}
	100% {left: 400%}
`;

type ButtonAnimation = {
  keyframes: Keyframes;
  duration: number;
  delay?: number;
};
export interface ScButtonProps {
  animation?: ButtonAnimation;
  link?: boolean;

  primary?: boolean;
  secondary?: boolean;
  secondaryTransparent?: boolean;
  tertiary?: boolean;
  blackTransparent?: boolean;
  white?: boolean;
  whiteTransparent?: boolean;
  grayTransparent?: boolean;
  transparent?: boolean;
  grayTransparentBg?: boolean;
  orangeRedGradient?: boolean;
  yellow?: boolean;
  amberYellow?: boolean;
  green?: boolean;
  orange?: boolean;
  sandGold?: boolean;

  greenGradient?: boolean;
  secondaryGreenGradient?: boolean;
  goldGradient?: boolean;
  darkGoldGradient?: boolean;
  goldenOrangeGradient?: boolean;

  disable?: boolean;

  xSmall?: boolean;
  small?: boolean;
  large?: boolean;
  xLarge?: boolean;

  autoPaddingHorizontal?: boolean;
  wide?: boolean;

  animated?: boolean;
  shimmer?: boolean;

  horizontalAlignment?: boolean;

  height?: string;
  paddingX?: number;
}

export const ScButton = styled.button<ScButtonProps>`
  display: inline-block;
  align-items: center;
  justify-content: center;
  position: relative;
  border: none;
  font-weight: bold;
  text-transform: uppercase;
  border-radius: 50px;
  border-width: 2px;
  border-style: solid;
  cursor: pointer;
  transition:
    box-shadow 0.3s,
    background-color 0.3s,
    border-color 0.3s,
    color 0.3s;
  padding: ${({ paddingX }) =>
    paddingX ? `0px ${paddingX}px 1px` : '0px 16px 1px 16px'};
  z-index: 0;
  overflow: hidden;

  ${({ animated }) =>
    animated &&
    css`
      animation: ${scale} 10s ease infinite;
      overflow: hidden;
      &:after {
        animation: ${shine} 10s infinite 0s;
        content: '';
        top: -500px;
        bottom: -500px;
        transform: rotate(45deg);
        width: 300%;
        position: absolute;
        z-index: 1;
        background: ${({ theme }) => `linear-gradient(
          to right,
          ${hexToRGB(theme.whiteColor, 0)} 0%,
          ${hexToRGB(theme.whiteColor, 1)} 100%
        )`};
      }
    `}

  ${({ shimmer }) =>
    shimmer &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -150%;
        width: 200%;
        height: 100%;
        background: linear-gradient(
          120deg,
          transparent 0%,
          rgba(255, 255, 255, 0.8) 50%,
          transparent 100%
        );
        transform: skewX(-20deg);
        animation: ${shimmerAnimate} 2.5s linear infinite;
        pointer-events: none;
        z-index: 2;
      }
    `}

  .text {
    position: relative;
    z-index: 1;
    .play-btn-content {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      color: ${({ theme }) => theme.whiteColor};
      text-transform: uppercase;
      font-weight: bold;
      font-size: 14px;
      border-radius: 40px;
      & > img {
        width: 15%;
        height: 37.5%;
        max-width: 16px;
        max-height: 18px;
        @media (max-width: 408px) {
          width: 20%;
          height: 50%;
        }
      }
      & > span {
        flex-shrink: 0;
      }
      @media (${device.tablet}) {
        font-size: 16px;
      }
      @media (${device.mobileXS}) {
        font-size: 14px;
      }
    }
  }
  .awareWrap {
    overflow: hidden;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: auto;
    height: auto;
    border-radius: 50px;
    position: absolute;
    overflow: hidden;
    display: flex;
    @media (max-width: 991px) {
      display: none;
    }
  }
  .aware {
    position: absolute;
    display: block;
    width: 0;
    height: 0;
    border-radius: 50px;
    transform: translate(-50%, -50%);
    transition:
      width 0.3s linear,
      height 0.3s linear,
      background-color 0.3s;
    z-index: 0;
  }
  &:hover {
    .aware {
      width: 250%;
      height: 250%;
    }
  }
  &:active {
    opacity: 0.8;
  }

  ${({ horizontalAlignment }) =>
    horizontalAlignment &&
    css`
      margin: 0 auto;
      display: flex;
    `}

  .text {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ animation }) =>
    animation &&
    css`
      animation: ${animation.keyframes} ${animation.duration}s
        ${animation.delay}s ease-in-out infinite;
    `}

  // sizes
  ///////////////

  // default
  ${({ xSmall, small, large, xLarge }) =>
    !(small || large || xLarge || xSmall) &&
    css`
      font-size: 16px;
      min-width: 144px;
      height: 48px;
    `}

  // xSmall

  ${({ xSmall }) =>
    xSmall &&
    css`
      font-size: 14px;
      min-width: 128px;
      height: 32px;
    `}

  // small
  ${({ small }) =>
    small &&
    css`
      font-size: 14px;
      min-width: 128px;
      height: 40px;
    `}

  // large
  ${({ large }) =>
    large &&
    css`
      font-size: 18px;
      min-width: 192px;
      height: 48px;
    `}

    ${({ xLarge }) =>
      xLarge &&
      css`
      font-size: 24px;
      min-width: 344px;
      line-height: 32px;
      height: 56px;
    `}

  // height
  ${({ height }) =>
    height &&
    css`
      height: ${height};
    `}

  // autoPaddingHorizontal

  ${({ autoPaddingHorizontal }) =>
    autoPaddingHorizontal &&
    css`
      min-width: auto;
    `}

  // wide

  ${({ wide }) =>
    wide &&
    css`
      display: block;
      width: 100%;
    `}

  // types
  ///////////////

  // default
  ${({
    primary,
    secondary,
    white,
    theme,
    orangeRedGradient,
    yellow,
    amberYellow,
    green,
    orange,
    sandGold,
    greenGradient,
    secondaryGreenGradient,
    goldGradient,
    darkGoldGradient,
    goldenOrangeGradient,
  }) =>
    !(
      primary ||
      secondary ||
      white ||
      orangeRedGradient ||
      yellow ||
      amberYellow ||
      green ||
      orange ||
      sandGold ||
      greenGradient ||
      secondaryGreenGradient ||
      goldGradient ||
      darkGoldGradient ||
      goldenOrangeGradient
    ) &&
    css`
      border-color: ${theme.whiteColor};
      background-color: transparent;
      color: ${theme.whiteColor};
      .aware {
        background-color: white;
      }
      &:not(:disabled):hover {
        color: ${theme.blackColor};
      }
      @media (${device.mobile}) {
        &:not(:disabled):hover {
          background-color: transparent;
          color: ${theme.whiteColor};
        }
      }
    `}

    // link
    ${({ link, theme }) =>
      link &&
      css`
      width: auto;
      height: auto;
      text-transform: none;
      font-weight: normal;
      border: none;
      background-color: transparent;
      color: ${theme.secondaryColor};

      &:not(:disabled):hover {
        color: ${theme.secondaryColorHover};
      }
    `}

    // primary
    ${({ primary, theme }) =>
      primary &&
      css`
      border-color: ${theme.mainColor};
      background-color: ${theme.mainColor};
      color: ${theme.whiteColor};
      &:before {
        top: 0px;
        bottom: 0;
        left: 0;
        right: 0;
        content: '';
        background-color: transparent;
        position: absolute;
        border-radius: 50px;
      }
      .aware {
        background-color: ${theme.mainColorHover};
      }
      &:hover {
        border-color: ${theme.mainColorHover};
      }
      &:active {
        opacity: 1;
        border-color: ${theme.mainColorActive};
        .text {
          opacity: 0.8;
        }
        .aware {
          background-color: ${theme.mainColorActive};
        }
      }
    `}

    ${({ orangeRedGradient, theme }) =>
      orangeRedGradient &&
      css`
      border: none;
      background: linear-gradient(90deg, #ffad0e 0%, #db0600 100%);
      color: ${theme.whiteColor};
      &:before {
        top: 0px;
        bottom: 0;
        left: 0;
        right: 0;
        content: '';
        background-color: transparent;
        position: absolute;
        border-radius: 50px;
      }
      .aware {
        background-color: ${theme.mainColorHover};
      }
      &:hover {
        border-color: ${theme.mainColorHover};
      }
      &:active {
        opacity: 1;
        border-color: ${theme.mainColorActive};
        .text {
          opacity: 0.8;
        }
        .aware {
          background-color: ${theme.mainColorActive};
        }
      }
    `}

    // secondary
    ${({ secondary, theme }) =>
      secondary &&
      css`
      border-color: ${theme.secondaryColor};
      background-color: ${theme.secondaryColor};
      color: ${theme.whiteColor};
      .aware {
        background-color: ${theme.secondaryColorHover};
      }
      &:hover {
        background-color: ${theme.secondaryColor};
        border-color: ${theme.secondaryColorHover};
      }
      &:active {
        opacity: 1;
        border-color: ${theme.secondaryColorActive};
        .text {
          opacity: 0.8;
        }
        .aware {
          background-color: ${theme.secondaryColorActive};
        }
      }
    `}

    // secondaryTransparent
    ${({ secondaryTransparent, theme }) =>
      secondaryTransparent &&
      css`
      border-color: ${theme.secondaryColor};
      background-color: transparent;
      color: ${theme.secondaryColor};
      .aware {
        background-color: ${theme.secondaryColor};
      }
      &:not(:disabled):hover {
        color: ${theme.whiteColor};
        @media (max-width: 991px) {
          color: ${theme.secondaryColor};
        }
      }
    `}

		// blackTransparent
    ${({ blackTransparent, theme }) =>
      blackTransparent &&
      css`
      border-color: ${theme.blackColor};
      background-color: transparent;
      color: ${theme.blackColor};
      .aware {
        background-color: ${theme.blackColor};
      }
      &:not(:disabled):hover {
        color: ${theme.whiteColor};
        @media (max-width: 991px) {
          color: ${theme.blackColor};
        }
      }
    `}

    // white
    ${({ white, theme }) =>
      white &&
      css`
      border-color: ${theme.whiteColor};
      background-color: ${theme.whiteColor};
      color: ${theme.blackColor};
    `}

    // whiteTransparent
    ${({ whiteTransparent, theme }) =>
      whiteTransparent &&
      css`
      border-color: ${theme.whiteColor};
      background-color: transparent;
      color: ${theme.whiteColor};
    `}

    // transparent
    ${({ transparent, theme }) =>
      transparent &&
      css`
      border-color: transparent;
      background-color: transparent;
      color: ${theme.blackColor};

      &:not(:disabled):hover {
        background-color: transparent;
        color: ${theme.blackColor};
      }
    `}

  ${({ grayTransparent, theme }) =>
    grayTransparent &&
    css`
      border-color: ${theme.grayColor};
      color: ${theme.grayColor};
      .aware {
        background-color: ${theme.grayColor};
      }
      &:not(:disabled):hover {
        color: ${theme.whiteColor};
        @media (max-width: 991px) {
          color: ${theme.grayColor};
        }
      }
    `}

  ${({ grayTransparentBg, theme }) =>
    grayTransparentBg &&
    css`
      border: none;
      color: ${theme.whiteColor};
      background-color: rgba(255, 255, 255, 0.2);
      .aware {
        background-color: rgba(255, 255, 255, 0.2);
      }
      @media (${device.mobile}) {
        &:hover {
          background-color: rgba(255, 255, 255, 0.4);
          border-color: ${theme.secondaryColorHover};
        }
      }
    `}

    ${({ yellow, theme }) =>
      yellow &&
      css`
      border: none;
      color: ${theme.blackColor};
      background-color: ${theme.yellowColor};
      transition: opacity 0.2s ease-in-out;
      .aware {
        background-color: ${theme.yellowColor};
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.8;
        }
      }
    `}

    ${({ amberYellow, theme }) =>
      amberYellow &&
      css`
      border: none;
      color: ${theme.whiteColor};
      background-color: ${theme.amberYellow};
      transition: opacity 0.2s ease-in-out;
      border-radius: 12px;
      &::before {
        content: '';
        inset: 5px;
        position: absolute;
        background: ${theme.amberGold};
        z-index: -1;
        border-radius: 6px;
      }
      .aware {
        background-color: ${theme.amberYellow};
        border-radius: 6px;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.8;
        }
      }
    `}

    ${({ green, theme }) =>
      green &&
      css`
      border: none;
      color: ${theme.whiteColor};
      background-color: ${theme.greenSuccessColor};
      transition: opacity 0.2s ease-in-out;
      .aware {
        background-color: ${theme.greenSuccessColor};
      }
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        border-radius: 50px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.3),
          rgba(255, 255, 255, 0)
        );
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: 2;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.8;
        }
      }
    `}

    ${({ secondaryGreenGradient, theme }) =>
      secondaryGreenGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.whiteColor};
      background: ${theme.secondaryGreenGradient};
      transition: opacity 0.2s ease-in-out;
      box-shadow: 0 0 15px ${theme.greenDarkGradient};
      z-index: 2;
      border-radius: 12px;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.3),
          rgba(255, 255, 255, 0)
        );
        border-radius: 12px;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: 2;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.9;
        }
      }
    `}

    ${({ orange, theme }) =>
      orange &&
      css`
      border: none;
      color: ${theme.whiteColor};
      background-color: ${theme.mainColor};
      transition: opacity 0.2s ease-in-out;
      .aware {
        background-color: ${theme.mainColorHover};
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 1;
        }
      }
    `}

    ${({ sandGold, theme }) =>
      sandGold &&
      css`
      position: relative;
      border: none;
      color: ${theme.whiteColor};
      background: ${theme.sandGold};
      height: 100%;
      transition: opacity 0.2s ease-in-out;
      z-index: 2;
      border-radius: 12px;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.3),
          rgba(255, 255, 255, 0)
        );
        border-radius: 12px;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: 2;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.9;
        }
      }
    `}

    ${({ greenGradient, theme }) =>
      greenGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.whiteColor};
      background: ${theme.greenDarkGradient};
      transition: opacity 0.2s ease-in-out;
      box-shadow: 0 0 15px ${theme.greenDarkGradient};
      font-size: 22px;
      font-weight: 700;
      line-height: 28px;
      letter-spacing: 0.44px;
      z-index: 1;

      &::before {
        content: '';
        inset: 2px;
        position: absolute;
        background: ${theme.greenLightGradient};
        z-index: -1;
        border-radius: 21px;
      }
      .aware {
        background: ${theme.greenLightGradient};
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.9;
        }
      }
    `}

    ${({ darkGoldGradient, theme }) =>
      darkGoldGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.cedarWoodColor};
      background: ${theme.goldDarkGradient};
      transition: opacity 0.2s ease-in-out;
      box-shadow: 0 0 10px #821f00;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      letter-spacing: 1%;
      &::before {
        content: '';
        inset: 0px;
        position: absolute;
        background: ${theme.darkGoldGradient};
        z-index: -1;
        border-radius: 30px;
      }
      .aware {
        background: ${theme.darkGoldGradient};
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 1;
        }
      }
    `}

    ${({ goldGradient, theme }) =>
      goldGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.blackColor};
      background: ${theme.goldDarkGradient};
      transition: opacity 0.2s ease-in-out;
      box-shadow: 0 0 10px #ffea7d;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      letter-spacing: 1%;
      &::before {
        content: '';
        inset: 2px;
        position: absolute;
        background: ${theme.goldLightGradient};
        z-index: -1;
        border-radius: 30px;
      }
      .aware {
        background: ${theme.goldLightGradient};
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 1;
        }
      }
    `}

    ${({ goldenOrangeGradient, theme }) =>
      goldenOrangeGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.blackColor};
      background: ${theme.goldenOrangeGradient};
      transition: opacity 0.2s ease-in-out;
      font-size: 14px;
      font-weight: 700;
      line-height: 24px;
      letter-spacing: 1%;
      &::before {
        content: '';
        inset: 2px;
        position: absolute;
        background: ${theme.goldenOrangeGradient};
        z-index: -1;
        border-radius: 30px;
      }
      .aware {
        background: ${theme.goldenOrangeGradient};
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 1;
        }
      }
    `}

    // disable
    ${({ disable, theme }) =>
      disable &&
      css`
      cursor: default;
      border-color: ${theme.grayLightColor};
      background-color: ${theme.grayLightColor};
      color: ${theme.grayColor};
      &:hover {
        box-shadow: none;
      }
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    .awareWrap {
      display: none;
    }
  }
`;

export const ScLink = styled(Link)`` as typeof Link;
